import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";

import { FormGroup } from "@cofano/cck";

import { FormDialog } from "components/dialogs/Dialogs";
import DatePicker from "components/cck/DatePicker";
import { FormErrorMessage } from "components/form/FormErrorMessage";

import LocationSelect from "view/locations/components/selects/LocationSelect";
import { callCreateOrEditSchema, CallCreateOrEditSchemaInput, CallCreateOrEditSchemaOutput } from "view/calls/utils/call.schema";

import { SaveCallDocument, SaveCallMutation } from "__generated__/view/calls/graphql/call.mutations.generated";
import { GeneralCallFragment } from "__generated__/view/calls/graphql/call.fragments.generated";

type CallAddDialogProps = {
	open: boolean;
	voyageId: number;
	onClose: () => void;
	refetch: () => void;
	call: (Pick<GeneralCallFragment, "id" | "pta" | "ptd"> & { location?: Pick<GeneralCallFragment["location"], "id" | "description"> | null }) | null
};

const CallCreateEditDialog = ({ open, voyageId, onClose, refetch, call }: CallAddDialogProps) => {

	const defaultValues = {
		id: null,
		location: null,
		pta: null,
		ptd: null,
	};

	const { control, handleSubmit, reset, watch } = useForm<CallCreateOrEditSchemaInput, unknown, CallCreateOrEditSchemaOutput>({
		resolver: zodResolver(callCreateOrEditSchema),
		defaultValues: call ?? defaultValues,
	});

	const [ pta, ptd ] = watch([ "pta", "ptd" ]);

	const [ save, { loading } ] = useMutation<SaveCallMutation>(SaveCallDocument);

	const handleOnClose = () => {
		onClose();
		reset(defaultValues);
	};

	const onSave = (data: CallCreateOrEditSchemaOutput) => {
		save({
			variables: {
				input: {
					id: data.id,
					locationId: data.location.id,
					pta: data.pta,
					ptd: data.ptd,
					voyageId: voyageId,
				},
			},
		}).then(() => {
			onClose();
			refetch();
		});
	};

	useEffect(() => {
		if (open && call) {
			reset({
				id: call.id,
				location: call.location,
				pta: call.pta ?? null,
				ptd: call.ptd ?? null,
			});
		}
	}, [ open ]);

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "CALL_ADD" })}
			form="call-add-form"
			loading={loading}>
			<form onSubmit={handleSubmit(onSave)} id="call-add-form">
				<FormGroup
					label={intl.formatMessage({ id: "LOCATION" })}
					labelPlacement="left-center"
					overflow="hidden"
					required>
					<Controller
						name="location"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<LocationSelect { ...field } />
								<FormErrorMessage name={field.name} errors={formState.errors}  />
							</React.Fragment>
						)} />
				</FormGroup>

				<FormGroup
					label={intl.formatMessage({ id: "PTA" })}
					labelPlacement="left-center"
					overflow="hidden"
					required>
					<Controller
						name="pta"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<DatePicker
									{ ...field}
									maxDate={ptd ? new Date(ptd) : undefined}
									showTimeInput
								/>
								<FormErrorMessage name={field.name} errors={formState.errors}  />
							</React.Fragment>
						)} />
				</FormGroup>

				<FormGroup
					label={intl.formatMessage({ id: "PTD" })}
					labelPlacement="left-center"
					overflow="hidden"
					required>
					<Controller
						name="ptd"
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<DatePicker
									{ ...field}
									minDate={pta ? new Date(pta) : undefined}
									showTimeInput
								/>
								<FormErrorMessage name={field.name} errors={formState.errors}  />
							</React.Fragment>
						)} />
				</FormGroup>
			</form>
		</FormDialog>
	);
};

export default CallCreateEditDialog;
